const PLATFORM_TAG_KEYS = new Set(['payment_platform', 'paymentPlatform', 'platform', 'payment_method', 'paymentMethod']);

export type MetricTags = Record<string, string | number | null | undefined>;
export type MetricsSummary = Record<
  string,
  {
    count?: number;
    eventCount?: number;
    uniqueActorCount?: number;
    total?: number;
    average?: number;
  }
>;

export type FunnelStep = {
  code: string;
  metricType: string;
  label: string;
};

export type FunnelRow = FunnelStep & {
  count: number;
  eventCount: number;
  uniqueActorCount: number;
  conversionFromPrevious: number | null;
  conversionFromFirst: number | null;
  isOutOfOrder: boolean;
};

export type FunnelImportantIssueSeverity = 'warning' | 'critical';

export type FunnelImportantIssue = {
  code: string;
  label: string;
  message: string;
  severity: FunnelImportantIssueSeverity;
};

export type FunnelSummaryResponse = {
  total?: MetricsSummary;
  platforms?: Record<string, MetricsSummary>;
  authStates?: Record<string, MetricsSummary>;
  features?: FeatureUsageSummaryItem[];
};

export type FeatureUsageSummaryItem = {
  key: string;
  label: string;
  eventCount: number;
  uniqueActorCount: number;
  totalValue: number;
  averageValue: number;
  metricTypes?: string[];
};

export type PageDwellTimeSummaryItem = {
  path: string;
  platform: string;
  eventCount: number;
  uniqueActorCount: number;
  totalSeconds: number;
  averageSeconds: number;
  maxSeconds: number;
};

export type PageDwellTimeSummaryResponse = {
  items?: PageDwellTimeSummaryItem[];
  platforms?: Record<string, PageDwellTimeSummaryItem[]>;
};

export type SajugMonitoringRoute = {
  method: string;
  path: string;
  active: number;
  total: number;
  errors: number;
  errorRate: number;
  latencyMs: {
    avg: number;
    p95: number;
    p99: number;
  };
};

export type SajugMonitoringSnapshot = {
  service: string;
  generatedAt: string;
  uptimeSeconds: number;
  activeUsers: {
    windowSeconds: number;
    current: number;
    authenticated: number;
    anonymous: number;
    todayPeak: number;
  };
  requests: {
    active: number;
    total: number;
    errors: number;
    errorRate: number;
    rps1m: number;
    latencyMs: {
      avg: number;
      p50: number;
      p95: number;
      p99: number;
    };
  };
  capacity: {
    status: 'normal' | 'watch' | 'saturated' | string;
    reasons: string[];
  };
  routes: SajugMonitoringRoute[];
};

export type SajugMonitoringCard = {
  label: string;
  value: string;
  detail: string;
  tone: 'normal' | 'watch' | 'saturated';
};

export type SajugTrafficBucket = {
  bucketStart: string;
  label: string;
  activeUsersPeak: number;
  authenticatedPeak: number;
  anonymousPeak: number;
  requests: number;
  errors: number;
  errorRate: number;
  activeRequestsPeak: number;
  avgLatencyMs: number;
};

export type SajugTrafficSummary = {
  peakActiveUsers: number;
  peakBucketStart: string | null;
  totalRequests: number;
  totalErrors: number;
  errorRate: number;
  busiestHourLabel: string;
};

export type SajugTrafficHistoryResponse = {
  service: string;
  generatedAt: string;
  bucketMinutes: number;
  windowHours: number;
  timezone: string;
  activeWindowMinutes?: number;
  summary: SajugTrafficSummary;
  buckets: SajugTrafficBucket[];
};

export type SajugErrorEvent = {
  occurredAt: number;
  occurredAtIso: string;
  method: string;
  path: string;
  statusCode: number | null;
  latencyMs: number | null;
  detail: string | null;
};

export type SajugRouteDailyError = {
  date: string;
  method: string;
  path: string;
  total: number;
  errors: number;
  errorRate: number;
  avgLatencyMs: number;
};

export type SajugErrorHistoryResponse = {
  service: string;
  generatedAt: string;
  windowDays: number;
  errorEvents: SajugErrorEvent[];
  routeDaily: SajugRouteDailyError[];
};

export type SajugTrafficChartRow = SajugTrafficBucket & {
  activeHeightPercent: number;
  requestHeightPercent: number;
  isPeak: boolean;
};

export type SajugTrafficDailyRow = {
  date: string;
  busiestLabel: string;
  bucketCount: number;
  peakActiveUsers: DailyMetricCell;
  authenticatedPeak: DailyMetricCell;
  anonymousPeak: DailyMetricCell;
  requests: DailyMetricCell;
  errors: DailyMetricCell;
  errorRate: DailyMetricCell;
  activeRequestsPeak: DailyMetricCell;
  avgLatencyMs: DailyMetricCell;
};

export type DailyActiveUserPoint = {
  date: string;
  total: number;
  web: number;
  mobile: number;
  unknown: number;
};

export type DailyActiveUsersResponse = {
  timezone: string;
  identitySemantics: string;
  startDate: string;
  endDate: string;
  summary: {
    current: number;
    previous: number;
    change: number;
    changePercent: number | null;
    average: number;
    peak: number;
    peakDate: string | null;
  };
  series: DailyActiveUserPoint[];
};

export type FeatureEngagementSource = 'feature' | 'route' | 'dwell';

export type FeatureEngagementRow = {
  key: string;
  label: string;
  sources: FeatureEngagementSource[];
  requestCount: number;
  uniqueActorCount: number;
  dwellTotalSeconds: number;
  dwellAverageSeconds: number;
  errorRate: number | null;
  p95LatencyMs: number | null;
};

export type FeatureEngagementInput = {
  features?: FeatureUsageSummaryItem[] | null;
  routes?: SajugMonitoringRoute[] | null;
  dwellItems?: PageDwellTimeSummaryItem[] | null;
};

export type AggregatedMetricData = Record<string, Record<string, number>>;

export type PlatformAggregatedMetricData = Record<string, Record<string, Record<string, number>>>;

export type DailyMetricTrend = 'up' | 'down' | 'flat';

export type DailyMetricCell = {
  value: number;
  change: number;
  changePercent: number | null;
  trend: DailyMetricTrend;
};

export type DailyMetricRow = {
  date: string;
  totalEvents: DailyMetricCell;
  appOpened: DailyMetricCell;
  formStarted: DailyMetricCell;
  resultViewed: DailyMetricCell;
  loginCompleted: DailyMetricCell;
  purchaseCompleted: DailyMetricCell;
  paymentFailed: DailyMetricCell;
  revenue: DailyMetricCell;
  dwellEvents: DailyMetricCell;
  dwellTotalSeconds: number;
  dwellAverageSeconds: DailyMetricCell;
};

export type PlatformDailyMetricBucket = {
  appOpened: DailyMetricCell;
  resultViewed: DailyMetricCell;
  loginCompleted: DailyMetricCell;
  dwellEvents: DailyMetricCell;
  dwellAverageSeconds: DailyMetricCell;
};

export type PlatformDailyMetricRow = {
  date: string;
  web: PlatformDailyMetricBucket;
  mobile: PlatformDailyMetricBucket;
  unknown: PlatformDailyMetricBucket;
};

export type AdFunnelMetricSummary = MetricsSummary;

export type AdFunnelDimensionSummary = {
  key: string;
  label: string;
  eventCount: number;
  metrics?: AdFunnelMetricSummary;
};

export type AdFunnelStep = {
  code: string;
  metricType: string;
  count: number;
  eventCount: number;
  conversionFromPrevious: number;
};

export type AdFunnelSummaryResponse = {
  totals?: AdFunnelMetricSummary;
  platforms?: Record<string, AdFunnelMetricSummary>;
  providers?: AdFunnelDimensionSummary[];
  placements?: AdFunnelDimensionSummary[];
  funnelSteps?: AdFunnelStep[];
  eventCountSemantics?: string;
};

export type DwellWeeklySummary = {
  currentStartDate: string;
  currentEndDate: string;
  previousStartDate: string | null;
  previousEndDate: string | null;
  dayCount: number;
  previousDayCount: number;
  totalSeconds: DailyMetricCell;
  averageSeconds: DailyMetricCell;
  events: DailyMetricCell;
};

type DailyMetricRawRow = {
  totalEvents: number;
  appOpened: number;
  formStarted: number;
  resultViewed: number;
  loginCompleted: number;
  purchaseCompleted: number;
  paymentFailed: number;
  revenue: number;
  dwellEvents: number;
  dwellTotalSeconds: number;
  dwellAverageSeconds: number;
};

type PlatformDailyMetricRawRow = {
  appOpened: number;
  resultViewed: number;
  loginCompleted: number;
  dwellEvents: number;
  dwellTotalSeconds: number;
  dwellAverageSeconds: number;
};

type SajugTrafficDailyRawRow = {
  date: string;
  busiestLabel: string;
  busiestRequests: number;
  bucketCount: number;
  peakActiveUsers: number;
  authenticatedPeak: number;
  anonymousPeak: number;
  requests: number;
  errors: number;
  errorRate: number;
  activeRequestsPeak: number;
  avgLatencyMs: number;
  latencyWeightedTotal: number;
};

export const FUNNEL_STEPS: FunnelStep[] = [
  { code: 'app_opened', metricType: 'APP_OPENED', label: '앱 열기' },
  { code: 'saju_form_started', metricType: 'SAJU_FORM_STARTED', label: '정보 입력 시작' },
  { code: 'saju_result_viewed', metricType: 'SAJU_RESULT_VIEWED', label: '결과 조회' },
  { code: 'login_completed', metricType: 'LOGIN_COMPLETED', label: '로그인/가입 완료' },
  { code: 'paywall_viewed', metricType: 'PAYWALL_VIEWED', label: '가격 화면 조회' },
  { code: 'purchase_completed', metricType: 'PURCHASE_COMPLETED', label: '구매 완료' },
];

export const FUNNEL_METRIC_TYPE_OPTIONS = [
  { value: 'APP_OPENED', label: '앱 열기' },
  { value: 'APP_FIRST_OPENED', label: '첫 앱 열기' },
  { value: 'SAJU_FORM_STARTED', label: '정보 입력 시작' },
  { value: 'SAJU_FORM_SUBMITTED', label: '정보 입력 완료' },
  { value: 'SAJU_PROFILE_SAVED', label: '프로필 저장' },
  { value: 'SAJU_RESULT_VIEWED', label: '결과 조회' },
  { value: 'LOGIN_STARTED', label: '로그인 시작' },
  { value: 'LOGIN_COMPLETED', label: '로그인 완료' },
  { value: 'SIGN_UP_COMPLETED', label: '가입 완료' },
  { value: 'PAYWALL_VIEWED', label: '가격 화면 조회' },
  { value: 'PURCHASE_STARTED', label: '구매 시작' },
  { value: 'PURCHASE_COMPLETED', label: '구매 완료' },
  { value: 'LOGIN_BENEFIT_MODAL_VIEWED', label: '혜택 모달 노출' },
  { value: 'LOGIN_BENEFIT_LOGIN_CLICKED', label: '혜택 모달 로그인 클릭' },
  { value: 'LOGIN_BENEFIT_GUEST_CLICKED', label: '혜택 모달 비로그인 클릭' },
  { value: 'BENEFIT_CAMPAIGN_VIEWED', label: '혜택 캠페인 노출' },
  { value: 'BENEFIT_CAMPAIGN_LOGIN_CLICKED', label: '혜택 캠페인 로그인 클릭' },
  { value: 'BENEFIT_CAMPAIGN_GUEST_CLICKED', label: '혜택 캠페인 비로그인 클릭' },
  { value: 'BENEFIT_CAMPAIGN_LOGIN_COMPLETED', label: '혜택 캠페인 로그인 완료' },
  { value: 'BENEFIT_TRIAL_GRANTED', label: '혜택 지급 완료' },
  { value: 'BENEFIT_TRIAL_SKIPPED', label: '혜택 지급 제외' },
  { value: 'PAGE_DWELL_TIME', label: '페이지 체류 시간' },
];

const asText = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const text = String(value).trim();
  return text.length > 0 ? text : null;
};

const formatCompactNumber = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '0';
  }
  return value.toLocaleString('ko-KR', { maximumFractionDigits: 1 });
};

const formatCompactPercent = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '0';
  }
  return value.toLocaleString('ko-KR', { maximumFractionDigits: 2 });
};

const formatMilliseconds = (value?: number | null) => `${formatCompactNumber(Math.round(value ?? 0))}ms`;

const roundMetricNumber = (value: number, fractionDigits = 2) => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const rounded = Number(value.toFixed(fractionDigits));
  return Object.is(rounded, -0) ? 0 : rounded;
};

const formatActiveWindow = (seconds: number) => {
  if (seconds < 60) {
    return `${seconds}초`;
  }
  const minutes = Math.round(seconds / 60);
  return `${minutes}분`;
};

const formatCapacityStatus = (status: SajugMonitoringSnapshot['capacity']['status']) => {
  if (status === 'saturated') {
    return '한계';
  }
  if (status === 'watch') {
    return '주의';
  }
  return '정상';
};

const normalizeCapacityTone = (status: SajugMonitoringSnapshot['capacity']['status']): SajugMonitoringCard['tone'] => {
  if (status === 'saturated') {
    return 'saturated';
  }
  if (status === 'watch') {
    return 'watch';
  }
  return 'normal';
};

export const buildSajugMonitoringCards = (snapshot: SajugMonitoringSnapshot): SajugMonitoringCard[] => {
  const tone = normalizeCapacityTone(snapshot.capacity.status);
  const activeWindow = formatActiveWindow(snapshot.activeUsers.windowSeconds);

  return [
    {
      label: '현재 사주 동접',
      value: `${formatCompactNumber(snapshot.activeUsers.current)}명`,
      detail: `최근 ${activeWindow} · 로그인 ${formatCompactNumber(snapshot.activeUsers.authenticated)} / 익명 ${formatCompactNumber(snapshot.activeUsers.anonymous)}`,
      tone: snapshot.activeUsers.current >= snapshot.activeUsers.todayPeak && snapshot.activeUsers.current > 0 ? 'watch' : 'normal',
    },
    {
      label: '오늘 최대 동접',
      value: `${formatCompactNumber(snapshot.activeUsers.todayPeak)}명`,
      detail: '사주 앱/API 활성 사용자 기준',
      tone: 'normal',
    },
    {
      label: '처리 중 요청',
      value: `${formatCompactNumber(snapshot.requests.active)}개`,
      detail: `최근 1분 ${formatCompactNumber(snapshot.requests.rps1m)} RPS`,
      tone: snapshot.requests.active >= 50 ? 'watch' : 'normal',
    },
    {
      label: 'p95 응답시간',
      value: formatMilliseconds(snapshot.requests.latencyMs.p95),
      detail: `평균 ${formatMilliseconds(snapshot.requests.latencyMs.avg)} · p99 ${formatMilliseconds(snapshot.requests.latencyMs.p99)}`,
      tone: snapshot.requests.latencyMs.p95 >= 3000 ? 'watch' : 'normal',
    },
    {
      label: '오류율',
      value: `${formatCompactNumber(snapshot.requests.errorRate)}%`,
      detail: `${formatCompactNumber(snapshot.requests.errors)} / ${formatCompactNumber(snapshot.requests.total)} 요청`,
      tone: snapshot.requests.errorRate >= 5 ? 'saturated' : snapshot.requests.errorRate >= 1 ? 'watch' : 'normal',
    },
    {
      label: '상태',
      value: formatCapacityStatus(snapshot.capacity.status),
      detail: snapshot.capacity.reasons.length > 0 ? snapshot.capacity.reasons.join(', ') : '특이사항 없음',
      tone,
    },
  ];
};

const heightPercent = (value: number, maxValue: number) => {
  if (value <= 0 || maxValue <= 0) {
    return 0;
  }
  return Math.max(4, Math.round((value / maxValue) * 100));
};

export const buildSajugTrafficChartRows = (
  traffic: SajugTrafficHistoryResponse | null | undefined
): SajugTrafficChartRow[] => {
  const buckets = traffic?.buckets ?? [];
  const maxActiveUsers = Math.max(0, ...buckets.map((bucket) => bucket.activeUsersPeak));
  const maxRequests = Math.max(0, ...buckets.map((bucket) => bucket.requests));

  return buckets.map((bucket) => ({
    ...bucket,
    activeHeightPercent: heightPercent(bucket.activeUsersPeak, maxActiveUsers),
    requestHeightPercent: heightPercent(bucket.requests, maxRequests),
    isPeak: maxActiveUsers > 0 && bucket.activeUsersPeak === maxActiveUsers,
  }));
};

const formatTrafficHour = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return `${parsed.getHours().toString().padStart(2, '0')}시`;
};

const trafficToneByPeak = (peak: number): SajugMonitoringCard['tone'] => {
  if (peak >= 80) {
    return 'saturated';
  }
  if (peak >= 40) {
    return 'watch';
  }
  return 'normal';
};

const trafficScaleSignal = (peak: number): Pick<SajugMonitoringCard, 'value' | 'detail' | 'tone'> => {
  if (peak >= 80) {
    return { value: '증설 필요', detail: '피크 동접 80명 이상', tone: 'saturated' };
  }
  if (peak >= 40) {
    return { value: '증설 검토', detail: '피크 동접 40명 이상', tone: 'watch' };
  }
  if (peak >= 20) {
    return { value: '주시', detail: '피크 동접 20명 이상', tone: 'watch' };
  }
  return { value: '여유', detail: '현재 기준 추가 증설 신호 없음', tone: 'normal' };
};

export const buildSajugTrafficSummaryCards = (
  traffic: SajugTrafficHistoryResponse | null | undefined
): SajugMonitoringCard[] => {
  if (!traffic) {
    return [];
  }

  const peak = traffic.summary.peakActiveUsers;
  const errorTone: SajugMonitoringCard['tone'] =
    traffic.summary.errorRate >= 5 ? 'saturated' : traffic.summary.errorRate >= 1 ? 'watch' : 'normal';
  const scaleSignal = trafficScaleSignal(peak);
  const activeWindowMinutes = traffic.activeWindowMinutes ?? 5;

  return [
    {
      label: '피크 시간대',
      value: traffic.summary.busiestHourLabel || formatTrafficHour(traffic.summary.peakBucketStart),
      detail: `최근 ${formatCompactNumber(traffic.windowHours)}시간 기준`,
      tone: 'normal',
    },
    {
      label: '피크 동접',
      value: `${formatCompactNumber(peak)}명`,
      detail: `${formatCompactNumber(activeWindowMinutes)}분 활성 사용자 피크`,
      tone: trafficToneByPeak(peak),
    },
    {
      label: '사주 요청량',
      value: `${formatCompactNumber(traffic.summary.totalRequests)}회`,
      detail: `오류 ${formatCompactNumber(traffic.summary.totalErrors)}회 · 오류율 ${formatCompactPercent(traffic.summary.errorRate)}%`,
      tone: errorTone,
    },
    {
      label: '운용 신호',
      value: scaleSignal.value,
      detail: scaleSignal.detail,
      tone: scaleSignal.tone,
    },
  ];
};

const aggregatedMetricNumber = (values: Record<string, number> | undefined, key: string) => {
  const value = values?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

const aggregatedMetricCount = (values: Record<string, number> | undefined, metricType: string) => (
  aggregatedMetricNumber(values, `${metricType}_count`)
);

const aggregatedMetricTotal = (values: Record<string, number> | undefined, metricType: string) => (
  aggregatedMetricNumber(values, `${metricType}_total`)
);

const totalAggregatedEvents = (values: Record<string, number> | undefined) => (
  Object.entries(values ?? {}).reduce((total, [key, value]) => (
    key.endsWith('_count') && Number.isFinite(value) ? total + value : total
  ), 0)
);

const dailyMetricCell = (value: number, previousValue: number | null): DailyMetricCell => {
  const normalizedValue = roundMetricNumber(value);
  if (previousValue === null) {
    return {
      value: normalizedValue,
      change: 0,
      changePercent: null,
      trend: 'flat',
    };
  }

  const change = roundMetricNumber(normalizedValue - previousValue);
  const changePercent = previousValue === 0
    ? (change === 0 ? 0 : 100)
    : roundMetricNumber((change / previousValue) * 100);

  return {
    value: normalizedValue,
    change,
    changePercent,
    trend: change > 0 ? 'up' : change < 0 ? 'down' : 'flat',
  };
};

const trafficDateKey = (bucketStart: string): string | null => {
  const directDate = bucketStart.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (directDate) {
    return directDate;
  }
  const parsed = new Date(bucketStart);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString().slice(0, 10);
};

export const buildSajugTrafficDailyRows = (
  traffic: SajugTrafficHistoryResponse | null | undefined
): SajugTrafficDailyRow[] => {
  const dailyRows = new Map<string, SajugTrafficDailyRawRow>();

  for (const bucket of traffic?.buckets ?? []) {
    const date = trafficDateKey(bucket.bucketStart);
    if (!date) {
      continue;
    }
    const raw = dailyRows.get(date) ?? {
      date,
      busiestLabel: '-',
      busiestRequests: 0,
      bucketCount: 0,
      peakActiveUsers: 0,
      authenticatedPeak: 0,
      anonymousPeak: 0,
      requests: 0,
      errors: 0,
      errorRate: 0,
      activeRequestsPeak: 0,
      avgLatencyMs: 0,
      latencyWeightedTotal: 0,
    };
    const shouldReplaceBusiest =
      bucket.activeUsersPeak > raw.peakActiveUsers
      || (bucket.activeUsersPeak === raw.peakActiveUsers && bucket.requests > raw.busiestRequests);

    raw.bucketCount += 1;
    raw.requests += bucket.requests;
    raw.errors += bucket.errors;
    raw.peakActiveUsers = Math.max(raw.peakActiveUsers, bucket.activeUsersPeak);
    raw.authenticatedPeak = Math.max(raw.authenticatedPeak, bucket.authenticatedPeak);
    raw.anonymousPeak = Math.max(raw.anonymousPeak, bucket.anonymousPeak);
    raw.activeRequestsPeak = Math.max(raw.activeRequestsPeak, bucket.activeRequestsPeak);
    raw.latencyWeightedTotal += bucket.avgLatencyMs * bucket.requests;
    if (shouldReplaceBusiest) {
      raw.busiestLabel = bucket.label;
      raw.busiestRequests = bucket.requests;
    }
    dailyRows.set(date, raw);
  }

  let previous: SajugTrafficDailyRawRow | null = null;
  return [...dailyRows.values()]
    .filter((raw) => raw.requests > 0 || raw.peakActiveUsers > 0 || raw.activeRequestsPeak > 0)
    .sort((left, right) => left.date.localeCompare(right.date))
    .map((raw) => {
      raw.errorRate = raw.requests > 0 ? roundMetricNumber((raw.errors / raw.requests) * 100) : 0;
      raw.avgLatencyMs = raw.requests > 0 ? roundMetricNumber(raw.latencyWeightedTotal / raw.requests) : 0;

      const row: SajugTrafficDailyRow = {
        date: raw.date,
        busiestLabel: raw.busiestLabel,
        bucketCount: raw.bucketCount,
        peakActiveUsers: dailyMetricCell(raw.peakActiveUsers, previous?.peakActiveUsers ?? null),
        authenticatedPeak: dailyMetricCell(raw.authenticatedPeak, previous?.authenticatedPeak ?? null),
        anonymousPeak: dailyMetricCell(raw.anonymousPeak, previous?.anonymousPeak ?? null),
        requests: dailyMetricCell(raw.requests, previous?.requests ?? null),
        errors: dailyMetricCell(raw.errors, previous?.errors ?? null),
        errorRate: dailyMetricCell(raw.errorRate, previous?.errorRate ?? null),
        activeRequestsPeak: dailyMetricCell(raw.activeRequestsPeak, previous?.activeRequestsPeak ?? null),
        avgLatencyMs: dailyMetricCell(raw.avgLatencyMs, previous?.avgLatencyMs ?? null),
      };
      previous = raw;
      return row;
    });
};

const platformDailyMetricRaw = (
  values: Record<string, number> | undefined,
): PlatformDailyMetricRawRow => {
  const dwellEvents = aggregatedMetricCount(values, 'PAGE_DWELL_TIME');
  const dwellTotalSeconds = aggregatedMetricTotal(values, 'PAGE_DWELL_TIME');
  return {
    appOpened: aggregatedMetricCount(values, 'APP_OPENED'),
    resultViewed: aggregatedMetricCount(values, 'SAJU_RESULT_VIEWED'),
    loginCompleted: aggregatedMetricCount(values, 'LOGIN_COMPLETED'),
    dwellEvents,
    dwellTotalSeconds: roundMetricNumber(dwellTotalSeconds),
    dwellAverageSeconds: dwellEvents > 0 ? dwellTotalSeconds / dwellEvents : 0,
  };
};

const platformDailyMetricCells = (
  raw: PlatformDailyMetricRawRow,
  previous: PlatformDailyMetricRawRow | null,
) => ({
  appOpened: dailyMetricCell(raw.appOpened, previous?.appOpened ?? null),
  resultViewed: dailyMetricCell(raw.resultViewed, previous?.resultViewed ?? null),
  loginCompleted: dailyMetricCell(raw.loginCompleted, previous?.loginCompleted ?? null),
  dwellEvents: dailyMetricCell(raw.dwellEvents, previous?.dwellEvents ?? null),
  dwellAverageSeconds: dailyMetricCell(raw.dwellAverageSeconds, previous?.dwellAverageSeconds ?? null),
});

export const buildPlatformDailyMetricRows = (
  data: PlatformAggregatedMetricData = {},
): PlatformDailyMetricRow[] => {
  const dates = Object.keys(data).sort();
  let previousWeb: PlatformDailyMetricRawRow | null = null;
  let previousMobile: PlatformDailyMetricRawRow | null = null;
  let previousUnknown: PlatformDailyMetricRawRow | null = null;

  return dates.map((date) => {
    const day = data[date] ?? {};
    const webRaw = platformDailyMetricRaw(day['web']);
    const mobileRaw = platformDailyMetricRaw(day['mobile']);
    const unknownRaw = platformDailyMetricRaw(day['unknown']);
    const row: PlatformDailyMetricRow = {
      date,
      web: platformDailyMetricCells(webRaw, previousWeb),
      mobile: platformDailyMetricCells(mobileRaw, previousMobile),
      unknown: platformDailyMetricCells(unknownRaw, previousUnknown),
    };
    previousWeb = webRaw;
    previousMobile = mobileRaw;
    previousUnknown = unknownRaw;
    return row;
  });
};

export const buildDailyMetricRows = (data: AggregatedMetricData = {}): DailyMetricRow[] => {
  const dates = Object.keys(data).sort();
  let previous: DailyMetricRawRow | null = null;

  return dates.map((date) => {
    const values = data[date];
    const dwellEvents = aggregatedMetricCount(values, 'PAGE_DWELL_TIME');
    const dwellTotalSeconds = aggregatedMetricTotal(values, 'PAGE_DWELL_TIME');
    const dwellAverageSeconds = dwellEvents > 0 ? dwellTotalSeconds / dwellEvents : 0;
    const raw = {
      totalEvents: totalAggregatedEvents(values),
      appOpened: aggregatedMetricCount(values, 'APP_OPENED'),
      formStarted: aggregatedMetricCount(values, 'SAJU_FORM_STARTED'),
      resultViewed: aggregatedMetricCount(values, 'SAJU_RESULT_VIEWED'),
      loginCompleted: aggregatedMetricCount(values, 'LOGIN_COMPLETED'),
      purchaseCompleted: aggregatedMetricCount(values, 'PURCHASE_COMPLETED'),
      paymentFailed: aggregatedMetricCount(values, 'PAYMENT_FAILED'),
      revenue: aggregatedMetricTotal(values, 'REVENUE'),
      dwellEvents,
      dwellTotalSeconds: roundMetricNumber(dwellTotalSeconds),
      dwellAverageSeconds,
    };

    const row: DailyMetricRow = {
      date,
      totalEvents: dailyMetricCell(raw.totalEvents, previous?.totalEvents ?? null),
      appOpened: dailyMetricCell(raw.appOpened, previous?.appOpened ?? null),
      formStarted: dailyMetricCell(raw.formStarted, previous?.formStarted ?? null),
      resultViewed: dailyMetricCell(raw.resultViewed, previous?.resultViewed ?? null),
      loginCompleted: dailyMetricCell(raw.loginCompleted, previous?.loginCompleted ?? null),
      purchaseCompleted: dailyMetricCell(raw.purchaseCompleted, previous?.purchaseCompleted ?? null),
      paymentFailed: dailyMetricCell(raw.paymentFailed, previous?.paymentFailed ?? null),
      revenue: dailyMetricCell(raw.revenue, previous?.revenue ?? null),
      dwellEvents: dailyMetricCell(raw.dwellEvents, previous?.dwellEvents ?? null),
      dwellTotalSeconds: raw.dwellTotalSeconds,
      dwellAverageSeconds: dailyMetricCell(raw.dwellAverageSeconds, previous?.dwellAverageSeconds ?? null),
    };

    previous = raw;
    return row;
  });
};

const summarizeDwellRows = (rows: DailyMetricRow[]) => {
  const events = rows.reduce((total, row) => total + row.dwellEvents.value, 0);
  const totalSeconds = roundMetricNumber(rows.reduce((total, row) => total + row.dwellTotalSeconds, 0));
  return {
    events,
    totalSeconds,
    averageSeconds: events > 0 ? roundMetricNumber(totalSeconds / events) : 0,
  };
};

export const buildDwellWeeklySummary = (
  rows: DailyMetricRow[],
  windowDays = 7,
): DwellWeeklySummary | null => {
  const sortedRows = [...rows]
    .filter((row) => row.date)
    .sort((left, right) => left.date.localeCompare(right.date));
  if (sortedRows.length === 0) {
    return null;
  }

  const safeWindowDays = Math.max(1, Math.floor(windowDays));
  const currentRows = sortedRows.slice(-safeWindowDays);
  const previousRows = sortedRows.slice(-safeWindowDays * 2, -safeWindowDays);
  const current = summarizeDwellRows(currentRows);
  const previous = summarizeDwellRows(previousRows);

  return {
    currentStartDate: currentRows[0]?.date ?? '',
    currentEndDate: currentRows[currentRows.length - 1]?.date ?? '',
    previousStartDate: previousRows[0]?.date ?? null,
    previousEndDate: previousRows[previousRows.length - 1]?.date ?? null,
    dayCount: currentRows.length,
    previousDayCount: previousRows.length,
    totalSeconds: dailyMetricCell(current.totalSeconds, previousRows.length > 0 ? previous.totalSeconds : null),
    averageSeconds: dailyMetricCell(current.averageSeconds, previousRows.length > 0 ? previous.averageSeconds : null),
    events: dailyMetricCell(current.events, previousRows.length > 0 ? previous.events : null),
  };
};

const summaryCount = (summary: MetricsSummary, code: string): number => {
  const count = summary[code]?.count;
  return typeof count === 'number' && Number.isFinite(count) ? count : 0;
};

const summaryOptionalCount = (summary: MetricsSummary, code: string, key: 'eventCount' | 'uniqueActorCount'): number | null => {
  const count = summary[code]?.[key];
  return typeof count === 'number' && Number.isFinite(count) ? count : null;
};

const percent = (value: number, base: number): number => {
  if (base <= 0) {
    return value > 0 ? 100 : 0;
  }
  return Number(((value / base) * 100).toFixed(2));
};

export const buildFunnelRows = (summary: MetricsSummary = {}): FunnelRow[] => {
  let previousCount = 0;
  const firstCount = summaryCount(summary, FUNNEL_STEPS[0]?.code ?? '');

  return FUNNEL_STEPS.map((step, index) => {
    const count = summaryCount(summary, step.code);
    const eventCount = summaryOptionalCount(summary, step.code, 'eventCount') ?? count;
    const uniqueActorCount = summaryOptionalCount(summary, step.code, 'uniqueActorCount') ?? count;
    const isOutOfOrder = index > 0 && count > previousCount;
    const canCompareToPrevious = index === 0 || !isOutOfOrder;
    const canCompareToFirst = index === 0 || (!isOutOfOrder && (firstCount <= 0 || count <= firstCount));
    const row: FunnelRow = {
      ...step,
      count,
      eventCount,
      uniqueActorCount,
      conversionFromPrevious: canCompareToPrevious ? (index === 0 ? 100 : percent(count, previousCount)) : null,
      conversionFromFirst: canCompareToFirst ? (index === 0 ? 100 : percent(count, firstCount)) : null,
      isOutOfOrder,
    };
    previousCount = count;
    return row;
  });
};

export const buildAnonymousDeviceFunnelRows = (
  funnelSummary: FunnelSummaryResponse | null | undefined
): FunnelRow[] => buildFunnelRows(funnelSummary?.authStates?.['anonymous'] ?? {});

const featureRow = (key: string, label: string): FeatureEngagementRow => ({
  key,
  label,
  sources: [],
  requestCount: 0,
  uniqueActorCount: 0,
  dwellTotalSeconds: 0,
  dwellAverageSeconds: 0,
  errorRate: null,
  p95LatencyMs: null,
});

const pushSource = (row: FeatureEngagementRow, source: FeatureEngagementSource) => {
  if (!row.sources.includes(source)) {
    row.sources.push(source);
  }
};

const weightedDwellAverage = (currentTotal: number, currentAverage: number, nextTotal: number, nextAverage: number) => {
  if (currentTotal <= 0) {
    return nextAverage;
  }
  const nextCombinedTotal = currentTotal + nextTotal;
  if (nextCombinedTotal <= 0) {
    return 0;
  }
  return roundMetricNumber(((currentAverage * currentTotal) + (nextAverage * nextTotal)) / nextCombinedTotal);
};

export const buildFeatureEngagementRows = ({
  features,
  routes,
  dwellItems,
}: FeatureEngagementInput): FeatureEngagementRow[] => {
  const rows = new Map<string, FeatureEngagementRow>();

  for (const route of routes ?? []) {
    const key = `route:${route.path}`;
    const row = rows.get(key) ?? featureRow(key, `${route.method} ${route.path}`);
    pushSource(row, 'route');
    row.requestCount += route.total;
    row.errorRate = route.errorRate;
    row.p95LatencyMs = route.latencyMs.p95;
    rows.set(key, row);
  }

  for (const feature of features ?? []) {
    const isPathFeature = feature.key.startsWith('path:');
    const key = isPathFeature ? `dwell:${feature.label || feature.key.slice(5)}` : feature.key || `feature:${feature.label}`;
    const row = rows.get(key) ?? featureRow(key, feature.label || key);
    pushSource(row, isPathFeature ? 'dwell' : 'feature');
    row.requestCount = isPathFeature
      ? Math.max(row.requestCount, feature.eventCount)
      : row.requestCount + feature.eventCount;
    row.uniqueActorCount = Math.max(row.uniqueActorCount, feature.uniqueActorCount);
    if (isPathFeature) {
      row.dwellTotalSeconds = Math.max(row.dwellTotalSeconds, roundMetricNumber(feature.totalValue));
      row.dwellAverageSeconds = Math.max(row.dwellAverageSeconds, roundMetricNumber(feature.averageValue));
    }
    rows.set(key, row);
  }

  for (const dwell of dwellItems ?? []) {
    const key = `dwell:${dwell.path}`;
    const row = rows.get(key) ?? featureRow(key, dwell.path);
    pushSource(row, 'dwell');
    row.requestCount = row.sources.includes('feature')
      ? row.requestCount + dwell.eventCount
      : Math.max(row.requestCount, dwell.eventCount);
    row.uniqueActorCount = Math.max(row.uniqueActorCount, dwell.uniqueActorCount);
    if (row.dwellTotalSeconds > 0 && row.dwellTotalSeconds !== dwell.totalSeconds) {
      row.dwellAverageSeconds = weightedDwellAverage(
        row.dwellTotalSeconds,
        row.dwellAverageSeconds,
        dwell.totalSeconds,
        dwell.averageSeconds
      );
      row.dwellTotalSeconds = roundMetricNumber(row.dwellTotalSeconds + dwell.totalSeconds);
    } else {
      row.dwellTotalSeconds = Math.max(row.dwellTotalSeconds, roundMetricNumber(dwell.totalSeconds));
      row.dwellAverageSeconds = Math.max(row.dwellAverageSeconds, roundMetricNumber(dwell.averageSeconds));
    }
    rows.set(key, row);
  }

  return [...rows.values()]
    .sort((left, right) => (
      right.requestCount - left.requestCount
      || right.dwellTotalSeconds - left.dwellTotalSeconds
      || left.label.localeCompare(right.label)
    ));
};

export const buildAdFunnelRows = (summary: AdFunnelMetricSummary = {}): AdFunnelStep[] => {
  const steps = [
    'ad_slot_eligible',
    'ad_slot_rendered',
    'ad_impression',
    'ad_reward_granted',
  ] as const;
  let previousCount = 0;

  return steps.map((code, index) => {
    const count = summaryCount(summary, code);
    const row: AdFunnelStep = {
      code,
      metricType: code.toUpperCase(),
      count,
      eventCount: summaryOptionalCount(summary, code, 'eventCount') ?? count,
      conversionFromPrevious: index === 0 ? 100 : percent(count, previousCount),
    };
    previousCount = count;
    return row;
  });
};

export const buildFunnelImportantIssues = (summary: MetricsSummary = {}): FunnelImportantIssue[] => {
  const rows = buildFunnelRows(summary);
  const rowByCode = new Map(rows.map((row) => [row.code, row]));
  const appOpened = rowByCode.get('app_opened');
  const formStarted = rowByCode.get('saju_form_started');
  const loginCompleted = rowByCode.get('login_completed');
  const paywallViewed = rowByCode.get('paywall_viewed');
  const purchaseCompleted = rowByCode.get('purchase_completed');
  const issues: FunnelImportantIssue[] = [];

  rows
    .filter((row) => row.isOutOfOrder)
    .forEach((row) => {
      issues.push({
        code: `out_of_order_${row.code}`,
        label: row.label,
        message: `${row.label} 값이 이전 단계보다 큽니다. 이벤트 집계 기준을 확인해야 합니다.`,
        severity: 'warning',
      });
    });

  if (
    appOpened
    && formStarted
    && appOpened.count >= 20
    && !formStarted.isOutOfOrder
    && formStarted.conversionFromFirst !== null
    && formStarted.conversionFromFirst < 10
  ) {
    issues.push({
      code: 'low_form_start_conversion',
      label: formStarted.label,
      message: `앱 열기 대비 정보 입력 시작 전환율이 ${formStarted.conversionFromFirst}%입니다.`,
      severity: 'critical',
    });
  }

  if (
    appOpened
    && loginCompleted
    && appOpened.count >= 20
    && !loginCompleted.isOutOfOrder
    && loginCompleted.conversionFromFirst !== null
    && loginCompleted.conversionFromFirst < 3
  ) {
    issues.push({
      code: 'low_login_conversion',
      label: loginCompleted.label,
      message: `앱 열기 대비 로그인/가입 완료 전환율이 ${loginCompleted.conversionFromFirst}%입니다.`,
      severity: 'critical',
    });
  }

  if (paywallViewed && purchaseCompleted && paywallViewed.count > 0 && purchaseCompleted.count === 0) {
    issues.push({
      code: 'zero_purchase_after_paywall',
      label: purchaseCompleted.label,
      message: `가격 화면 조회 ${paywallViewed.count}건이 있지만 구매 완료가 없습니다.`,
      severity: 'warning',
    });
  }

  return issues;
};

const normalizeMetricPaymentPlatform = (value: string | number | null | undefined): string | null => {
  const text = asText(value);
  if (!text) {
    return null;
  }
  const normalized = text.toUpperCase();
  if (normalized.includes('GOOGLE') || normalized.includes('PLAY') || normalized.includes('ANDROID')) {
    return 'GOOGLE_PLAY';
  }
  if (normalized.includes('NICE')) {
    return 'NICEPAY';
  }
  if (normalized.includes('PAYPLE')) {
    return 'PAYPLE';
  }
  if (normalized.includes('TOSS')) {
    return 'TOSS_PAYMENTS';
  }
  if (normalized === 'CARD' || normalized.includes('CARD') || normalized.includes('카드') || normalized.includes('BILLING_KEY')) {
    return 'NICEPAY';
  }
  return normalized;
};

export const formatMetricPaymentPlatform = (tags?: MetricTags): string => {
  const explicit = normalizeMetricPaymentPlatform(
    tags?.['payment_platform'] ?? tags?.['paymentPlatform'] ?? tags?.['platform'] ?? tags?.['payment_method'] ?? tags?.['paymentMethod']
  );
  if (explicit) {
    return formatMetricPaymentPlatformValue(explicit);
  }

  const orderId = asText(tags?.['order_id'] ?? tags?.['orderId']);
  if (orderId?.toUpperCase().startsWith('GPA.') || orderId?.toUpperCase().startsWith('IAP-')) {
    return 'Google Play Billing';
  }
  // Legacy NicePay orders have SUB_ ids and no recorded method; Paddle orders always carry PADDLE, checked above.
  if (orderId?.toUpperCase().startsWith('SUB_')) {
    return 'NicePay';
  }

  return '-';
};

export const formatMetricTags = (value?: MetricTags): string => {
  if (!value || Object.keys(value).length === 0) {
    return '-';
  }
  const entries = Object.entries(value).filter(([key]) => !PLATFORM_TAG_KEYS.has(key));
  if (entries.length === 0) {
    return '-';
  }
  return entries.map(([key, item]) => `${key}=${asText(item) ?? ''}`).join(', ');
};

const formatMetricPaymentPlatformValue = (value: string): string => {
  switch (value) {
    case 'GOOGLE_PLAY':
      return 'Google Play Billing';
    case 'NICEPAY':
      return 'NicePay';
    case 'PAYPLE':
      return 'Payple';
    case 'TOSS_PAYMENTS':
      return 'Toss Payments';
    case 'PADDLE':
      return 'Paddle';
    default:
      return value;
  }
};
